import { useEffect, useState } from 'react';

/**
 * Watch for resizes and return the window's size.
 * This triggers a rerender every time the window resizes.
 * For components with expensive renders, this could cause perfomance problems!
 *
 * Only register one actual window event handler at any given time,
 * as it can be used for all hook instances.
 *
 * **TODO:** It would probably be a good idea to allow a timeout
 * to be passed in to debounce the handler.
 */

type ScreenSize = {
  screenWidth: number;
  screenHeight: number;
};

// SSR defaults: Used for initial render during server-side rendering
// to ensure consistent hydration between server and client
const SSR_DEFAULTS: ScreenSize = {
  screenWidth: 1024,
  screenHeight: 768,
};

const listeners = new Set<(screenSize: ScreenSize) => void>();
const handler = () => {
  listeners.forEach((listener) =>
    listener({
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
    }),
  );
};

export const useScreenSize = (): ScreenSize => {
  // SSR-safe: Always use default values for initial render (both server and client)
  // to ensure hydration matches. Update to actual size in useEffect.
  const [size, setSize] = useState<ScreenSize>(SSR_DEFAULTS);

  useEffect(() => {
    // On client, immediately update to actual window size
    setSize({
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
    });

    listeners.add(setSize);
    if (listeners.size === 1) {
      window.addEventListener('resize', handler);
    }

    return () => {
      listeners.delete(setSize);
      if (listeners.size === 0) {
        window.removeEventListener('resize', handler);
      }
    };
  }, []);

  return size;
};

export default useScreenSize;
